/** @jsxImportSource @emotion/react */
import { faPlay } from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button as ModeButton,
    ButtonGroup,
    FormGroup,
    Label,
    Modal, ModalBody, ModalFooter, ModalHeader
} from "reactstrap";

import dayjs from "dayjs";
import { applyDisplayFilterFields } from "../../../../actions/Tenants/config/applyDisplayFilter";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { PROGRAM_KIND_LABELS, ProgramKind, programKindOf } from "../../../../constants/ripeningStorePrograms";
import { RipeningProgram, useRipeningPrograms } from "../../../../hooks/useRipeningPrograms";
import { toast } from "../../../../utils/toast";
import MetaForm, { metaFormIsValid } from "../../../Forms/MetaForm";
import SingleSelect from "../../../Forms/SingleSelect";
import { Button } from "../../../Helper/Button";
import { ConfirmationModal } from "../../../Helper/Modal";
import useRipeningRoom, { DATE_URL_FORMAT } from "../hooks/useRipeningRoom";
import { buildRipeningProgramSnapshot, deriveProgramEndDate, startOverrideErrors } from "./ripeningProgramCycle";
import RipeningProgramPreview from "./RipeningProgramPreview";
import StartCycleFeedbackModal from "./StartCycleFeedbackModal";

function durationLabel(minutes: number) {
    const days = minutes / (24 * 60);
    if (Number.isInteger(days)) return `${days} day${days === 1 ? "" : "s"}`;
    const hours = minutes / 60;
    if (Number.isInteger(hours)) return `${hours} hour${hours === 1 ? "" : "s"}`;
    return `${minutes} minutes`;
}

export default function StartRipeningCycleForm({ isInBottomBar }: { isInBottomBar?: boolean }) {
    const config = useConfig();
    const { fruit_type, currentLocation, startRipeningCycle, startRipeningCycleLoading, activeRipeningCycle, setSearchParam, } = useRipeningRoom();
    const [modalOpen, setModal] = useState(false);
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewProgram, setPreviewProgram] = useState<RipeningProgram | null>(null);
    // * The ripener picks ripening or storage first; only programs of that mode can be chosen.
    const [kind, setKind] = useState<ProgramKind>("ripening");

    const [query, setQuery] = useState<any>({});
    // * Without RipeWise the cycle starts unlinked: no program selection, fetch or program fields.
    const ripewiseEnabled = !!config.root_config?.ripening_room_enable_ripewise;
    const gatewayId = ripewiseEnabled ? currentLocation?.gateway : undefined;
    // The backend verifies the tenant against this room before calling the program service.
    const programsQuery = useRipeningPrograms(
        gatewayId,
        { tenantId: currentLocation?.tenant_id },
        fruit_type || undefined,
        modalOpen && !!fruit_type,
    );
    const programs = useMemo(
        () => (programsQuery.data?.results ?? []).filter((program) => program.active && programKindOf(program.definition) === kind),
        [programsQuery.data?.results, kind],
    );
    const kindLabel = PROGRAM_KIND_LABELS[kind].toLowerCase();
    const selectedProgram = programs.find((program) => program.id === query.ripening_program_id);
    const meta_form = useMemo(() => applyDisplayFilterFields(
        config.root_config?.ripening_room_start_cycle_form,
        { fruit_type },
    ).map((field) => (["ripening_start_date", "ripening_end_date"].includes(field.name)
        ? { ...field, disabled: !!selectedProgram }
        : field)), [config.root_config?.ripening_room_start_cycle_form, fruit_type, selectedProgram]);

    useEffect(() => {
        if (modalOpen) {
            setSearchParam("ripening_cycle_id", "");
            setPreviewModalOpen(false);
            setPreviewProgram(null);
            setKind("ripening");
            setQuery({
                ripening_start_date: dayjs().format(DATE_URL_FORMAT),
                ripening_end_date: dayjs().add(5, "day").format(DATE_URL_FORMAT),
                ripening_target: currentLocation?.ripening_target,
                season: currentLocation?.season,
                ...(ripewiseEnabled ? { idempotency_key: crypto.randomUUID() } : {}),
            });
        }
    }, [modalOpen]);

    // Hide if there already is an active or selected cycle; the feedback on the start of
    // that cycle's program stays available.
    if (activeRipeningCycle) {
        return <StartCycleFeedbackModal />;
    }

    const updateQueryValue = (previous, field, value, program: RipeningProgram | undefined = selectedProgram) => {
        const next = { ...previous, [field]: value };
        if (field === "ripening_start_date" && program) {
            next.ripening_end_date = deriveProgramEndDate(value, program.duration_minutes);
        }
        return next;
    };

    const setValue = (field, value) => setQuery((previous) => updateQueryValue(previous, field, value));

    const selectProgram = (_field: string, programId: string | null) => {
        const program = programs.find((item) => item.id === programId);
        setQuery((previous) => {
            if (!program) {
                const next = { ...previous };
                delete next.ripening_program_id;
                delete next.ripening_program;
                delete next.ripening_program_override;
                setPreviewModalOpen(false);
                setPreviewProgram(null);
                return next;
            }
            const immediateStartDate = dayjs().format(DATE_URL_FORMAT);
            return {
                ...previous,
                ripening_program_id: program.id,
                ripening_program: buildRipeningProgramSnapshot(program),
                ripening_program_override: null,
                ripening_start_date: immediateStartDate,
                ripening_end_date: deriveProgramEndDate(immediateStartDate, program.duration_minutes),
            };
        });
        setPreviewProgram(program ? JSON.parse(JSON.stringify(program)) : null);
    };

    const selectKind = (next: ProgramKind) => {
        if (next === kind) return;
        selectProgram("ripening_program_id", null);
        setKind(next);
    };

    const updatePreviewProgram = (program: RipeningProgram) => {
        setPreviewProgram(program);
        // * A storage program can only change its temperature at the start.
        if (programKindOf(program.definition) === "storage") {
            const setpoint = program.definition.ripewise?.store?.Store_Setpoint;
            setQuery((previous) => ({
                ...previous,
                ripening_program: buildRipeningProgramSnapshot(program),
                ripening_program_override: setpoint === undefined ? null : { store: { Store_Setpoint: setpoint } },
            }));
            return;
        }
        const phaseOverrides = (program.definition.ripewise?.phases ?? []).map((phase) => ({
            phase: phase.phase,
            values: Object.fromEntries(["T_End", "Spv_C2H4"]
                .filter((key) => phase.values[key] !== undefined)
                .map((key) => [key, phase.values[key]])),
        }));
        setQuery((previous) => ({
            ...previous,
            ripening_program: buildRipeningProgramSnapshot(program),
            ripening_program_override: { phases: phaseOverrides },
        }));
    };

    const onDebounce = (field, value) => {
        const field_config = meta_form.find((i) => i.name === field);
        if (metaFormIsValid([field_config], { [field]: value })) {
            return setQuery((previous) => updateQueryValue(previous, field, value));
        }
        return Promise.resolve();
    };

    const overrideErrors = startOverrideErrors(query.ripening_program_override);

    const onSubmit = () => {
        if (gatewayId && programsQuery.isError) {
            toast.error("Ripening programs are unavailable. Please try again later.");
            return;
        }
        if (gatewayId && !fruit_type) {
            toast.error("The room fruit type must be known before selecting a ripening program.");
            return;
        }
        if (gatewayId && programs.length === 0) {
            toast.error(`No active ${kindLabel} programs are configured for this ripening room.`);
            return;
        }
        if (gatewayId && !selectedProgram) {
            toast.error(`Please select a ${kindLabel} program before starting the cycle.`);
            return;
        }
        if (meta_form && !metaFormIsValid(meta_form, query)) return;
        setConfirmationModalOpen(true);
    };

    const onConfirmstartRipeningCycle = async () => {
        try {
            // * For a program start the start feedback opens next (see StartCycleFeedbackModal).
            await startRipeningCycle(query);
            setModal(false);
        } catch {
            // API errors are surfaced by the shared client; keep the form open for retry.
        }
    };

    const onCloseConfirmation = () => {
        setConfirmationModalOpen(false);
    };

    return <>
        <StartCycleFeedbackModal />
        <ConfirmationModal
            isOpen={confirmationModalOpen}
            onClose={onCloseConfirmation}
            onConfirm={onConfirmstartRipeningCycle}
            header="Confirm Ripening Cycle Start"
            confirmButtonText="Start Cycle"
            cancelButtonText="Cancel"
            size="md"
            color="primary"
        >
            {selectedProgram && kind === "storage" && "Are you sure you want to start storing immediately? The ripening room holds these storage settings until the cycle is ended, and the end date of all allocated pallets in this room is updated."}
            {selectedProgram && kind === "ripening" && "Are you sure you want to start this program immediately? The ripening room starts it now and the end date of all allocated pallets in this room is updated."}
            {!selectedProgram && "Are you sure you want to start the ripening program? This will update the end date of all allocated pallets in this room. Please make sure the allocated pallets in the room are correct."}
        </ConfirmationModal>

        <Modal isOpen={modalOpen} size="md">
            <ModalHeader >Start Ripening Cycle</ModalHeader>
            <ModalBody>
                <Alert color="info">You are changing the ripening program for this room. Before changing the program, please make sure the allocated pallets in the room are correct.</Alert>
                {gatewayId && programsQuery.isError && (
                    <Alert color="warning">
                        Ripening programs are unavailable. The cycle cannot be linked to a ripening program.
                    </Alert>
                )}
                {gatewayId && !fruit_type && (
                    <Alert color="warning">
                        The room fruit type is unknown. Allocate product before starting a ripening program.
                    </Alert>
                )}
                {gatewayId && programsQuery.isLoading && (
                    <div className="text-muted mb-3">Loading ripening programs...</div>
                )}
                {gatewayId && (
                    <FormGroup>
                        <Label id="start-cycle-mode" className="d-block">Mode *</Label>
                        <ButtonGroup aria-labelledby="start-cycle-mode">
                            {(Object.keys(PROGRAM_KIND_LABELS) as ProgramKind[]).map((option) => (
                                <ModeButton
                                    key={option}
                                    type="button"
                                    color="primary"
                                    outline={kind !== option}
                                    active={kind === option}
                                    aria-pressed={kind === option}
                                    data-testid={`start-mode-${option}`}
                                    onClick={() => selectKind(option)}
                                >
                                    {PROGRAM_KIND_LABELS[option]}
                                </ModeButton>
                            ))}
                        </ButtonGroup>
                        {kind === "storage" && <div className="small text-muted mt-1">The room holds one climate until the cycle is ended.</div>}
                    </FormGroup>
                )}
                {gatewayId && !programsQuery.isLoading && !programsQuery.isError && programs.length === 0 && (
                    <Alert color="warning">
                        No active {kindLabel} programs are configured for this ripening room.
                    </Alert>
                )}
                {gatewayId && programs.length > 0 && (
                    <>
                        <SingleSelect
                            field="ripening_program_id"
                            label={`${PROGRAM_KIND_LABELS[kind]} program *`}
                            description={(
                                <>
                                    Selecting a program starts it immediately and locks the expected start and end dates. {previewProgram && (
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0 align-baseline"
                                            onClick={() => setPreviewModalOpen(true)}
                                        >
                                            Preview &amp; adjust program
                                        </button>
                                    )}
                                </>
                            )}
                            options={programs.map((program) => ({
                                value: program.id,
                                label: kind === "storage" ? program.name : `${program.name} (${durationLabel(program.duration_minutes)})`,
                            }))}
                            post_value={query.ripening_program_id}
                            setValue={selectProgram}
                            isClearable
                        />
                    </>
                )}
                <MetaForm
                    meta={meta_form}
                    object={query}
                    config={config}
                    setValue={setValue}
                    onDebounce={onDebounce}
                    extra_context={{ fruit_type: fruit_type || undefined }}
                />
                {overrideErrors.length > 0 && (
                    <Alert color="danger" className="mb-0 mt-2" data-testid="start-override-errors">
                        {overrideErrors.map((error) => <div key={error}>{error}</div>)}
                    </Alert>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={() => setModal(false)}>Cancel</Button>
                <Button
                    color="primary"
                    onClick={onSubmit}
                    disabled={startRipeningCycleLoading || overrideErrors.length > 0 || (!!gatewayId && (
                        !fruit_type
                        || programsQuery.isLoading
                        || programsQuery.isError
                        || programs.length === 0
                        || !selectedProgram
                    ))}
                >
                    Start
                </Button>
            </ModalFooter>
        </Modal>

        <Modal isOpen={previewModalOpen} size="xl" toggle={() => setPreviewModalOpen(false)}>
            <ModalHeader toggle={() => setPreviewModalOpen(false)}>Preview &amp; adjust program</ModalHeader>
            <ModalBody>
                {previewProgram && (
                    <RipeningProgramPreview
                        program={previewProgram}
                        editable
                        onChange={updatePreviewProgram}
                    />
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={() => setPreviewModalOpen(false)}>Close</Button>
            </ModalFooter>
        </Modal>

        <Button
            className="text-nowrap"
            color="primary"
            // disabled={!isFilledArray(selectedLayersArray)}
            onClick={() => setModal(true)}
            icon={faPlay}
            isInBottomBar={isInBottomBar}
        >
            Start
        </Button>
    </>;

}
